<template>
    <div ref="previewElement" class="est-dropzone">
        <DropZone :key="dropzoneKey"
                  ref="dropzone"
                  :url="dropzoneOptions.url"
                  :method="dropzoneOptions.method || 'POST'"
                  :headers="dropzoneOptions.headers"
                  :param-name="dropzoneOptions.paramName || 'file'"
                  :max-files="dropzoneOptions.maxFiles || null"
                  :max-file-size="maxFileSize"
                  :accepted-files="acceptedFiles"
                  :upload-on-drop="dropzoneOptions.autoProcessQueue !== false"
                  :parallel-upload="dropzoneOptions.parallelUploads || 1"
                  @error-add="onAddError"
                  @sending="onSending">
            <template #message>
                <span>{{ defaultMessage }}</span>
            </template>
        </DropZone>
        <div v-show="visibleError" class="dz-error-message text-danger mt-2">{{ visibleError }}</div>
        <div class="dz-error-mark d-none"></div>
    </div>
</template>

<script>
// clientapp `components/shared/CompatDropzone.vue` kopyası (Aşama 4.5): legacy `vueDropzone` adı
// (plugins/vue-dropzone.js). `vdropzone-*` olayları ve `$refs.x.dropzone.{options,on,processFile,processQueue}`
// + `file.previewElement` sözleşmesi vue2-dropzone 3.6 ile aynıdır (Multimedia/FileUpload.vue: 401'de kuyruğu
// durdurup token yenilenince yeniden dener). Vega farkı: boş kutunun vue2-dropzone görünümünde
// olması; önizleme karoları clientapp'teki gibi. İstek
// (multipart `file` alanı, Accept/Cache-Control/X-Requested-With ve FileUpload başlıkları) vue2-dropzone'unkiyle aynı.
import DropZone from 'dropzone-vue';
import 'dropzone-vue/dist/dropzone-vue.common.css';

export default {
    name: 'CompatDropzone',
    components: { DropZone },
    props: {
        options: {
            type: Object,
            required: true
        }
    },
    emits: [
        'vdropzone-sending',
        'vdropzone-success',
        'vdropzone-error',
        'vdropzone-complete'
    ],
    data() {
        const dropzoneOptions = {
            ...this.options,
            headers: { ...(this.options.headers || {}) }
        };

        return {
            dropzoneOptions,
            eventHandlers: {
                error: [],
                success: []
            },
            activeRequests: new Map(),
            pendingSends: new Set(),
            cancelledFiles: new Set(),
            dropzoneKey: 0,
            visibleError: '',
            dropzone: {
                options: dropzoneOptions,
                on: this.onLegacyEvent,
                processFile: this.retryFile,
                processQueue: this.processQueue
            }
        };
    },
    computed: {
        maxFileSize() {
            return (this.dropzoneOptions.maxFilesize || 1) * 1024 * 1024;
        },
        acceptedFiles() {
            if (!this.dropzoneOptions.acceptedFiles) return null;

            return this.dropzoneOptions.acceptedFiles
                .split(',')
                .map(value => value.trim().replace('/*', ''))
                .filter(Boolean);
        },
        defaultMessage() {
            const message = this.dropzoneOptions.dictDefaultMessage || 'Drop here';
            return message.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        }
    },
    mounted() {
        this.$watch(() => this.$refs.dropzone?.all, this.tagFileTypes, {
            deep: true,
            flush: 'post',
            immediate: true
        });
    },
    methods: {
        // Küçük resmi olmayan dosyalarda (PDF, ofis dosyası) karo boş bir kutu kalıyor.
        // Paket önizleme içeriği için slot vermediğinden uzantı DOM'a yazılır, karoya
        // CSS basar (`attr(data-ext)`).
        tagFileTypes() {
            const items = Object.values(this.$refs.dropzone?.all || {});

            this.$el.querySelectorAll('.dropzone__item').forEach((element, index) => {
                const name = items[index]?.file?.name || '';
                const extension = name.includes('.') ? name.split('.').pop() : '';
                element.setAttribute('data-ext', extension.slice(0, 4).toUpperCase());
            });
        },
        onLegacyEvent(name, handler) {
            if (this.eventHandlers[name]) this.eventHandlers[name].push(handler);
        },
        notifyLegacy(name, ...args) {
            this.eventHandlers[name].forEach(handler => handler(...args));
        },
        ensureLegacyPreviewElement(file) {
            if (file.previewElement) return;
            file.previewElement = this.$refs.previewElement;
        },
        getAddErrorMessage(error) {
            const messages = {
                INVALID_TYPE: this.$t('InvalidFileType'),
                MAX_FILE_SIZE: this.$t('FileTooLarge'),
                MAX_FILE: this.$t('UploadFileLimitReached')
            };

            return messages[error] || this.$t('MediaUploadFailed');
        },
        onAddError({ files, error }) {
            const file = files?.[0];
            const message = this.getAddErrorMessage(error);
            const xhr = { status: 0 };

            this.visibleError = message;
            if (!file) return;

            this.ensureLegacyPreviewElement(file);
            this.$emit('vdropzone-error', file, message, xhr);
            this.notifyLegacy('error', file, message, xhr);
        },
        onSending(files, xhr, formData) {
            const file = files[0];
            this.ensureLegacyPreviewElement(file);
            this.visibleError = '';
            this.activeRequests.set(file, xhr);
            this.pendingSends.add(file);

            this.$emit('vdropzone-sending', file, xhr, formData);
            this.pendingSends.delete(file);

            if (this.cancelledFiles.delete(file)) {
                this.activeRequests.delete(file);
                Object.defineProperty(xhr, 'send', {
                    configurable: true,
                    value: () => {}
                });
                xhr.abort();
                return;
            }

            xhr.addEventListener('load', () => {
                const response = this.parseResponse(xhr);

                if (xhr.status >= 200 && xhr.status < 300) {
                    this.$emit('vdropzone-success', file, response);
                    this.notifyLegacy('success', file, response);
                } else {
                    this.emitUploadError(file, response, xhr);
                }
            });
            xhr.addEventListener('error', () => this.emitUploadError(file, null, xhr));
            xhr.addEventListener('loadend', () => {
                this.activeRequests.delete(file);
                this.$emit('vdropzone-complete', file);
            });
        },
        parseResponse(xhr) {
            if (typeof xhr.response === 'object' && xhr.response !== null) return xhr.response;

            const response = xhr.responseText || xhr.response;
            if (!response) return response;

            try {
                return JSON.parse(response);
            } catch {
                return response;
            }
        },
        emitUploadError(file, response, xhr) {
            const message = response || { message: xhr.statusText || 'Upload failed' };
            this.visibleError = typeof message === 'string'
                ? message
                : message.message || this.$t('MediaUploadFailed');
            this.$emit('vdropzone-error', file, message, xhr);
            this.notifyLegacy('error', file, message, xhr);
        },
        processQueue() {
            this.$refs.dropzone?.processQueue();
        },
        retryFile(file) {
            const item = Object.values(this.$refs.dropzone?.all || {})
                .find(candidate => candidate.file === file);

            if (!item) return;

            item.status = 'QUEUED';
            this.processQueue();
        },
        removeFile(file) {
            const entry = Object.entries(this.$refs.dropzone?.all || {})
                .find(([, item]) => item.file === file);

            const request = this.activeRequests.get(file);
            if (this.pendingSends.has(file)) this.cancelledFiles.add(file);
            else if (request) request.abort();

            if (!entry) return;

            if (file.type.startsWith('image/')) this.dropzoneKey += 1;
            else this.$refs.dropzone.removeFile(entry[0]);
        },
        removeAllFiles() {
            this.activeRequests.forEach(request => request.abort());
            this.dropzoneKey += 1;
        }
    }
};
</script>

<style scoped>
/* Vega: boş kutu vue2-dropzone 3.6 + Dropzone 5 CSS'indeki gibi (.vue-dropzone/.dropzone/.dz-message). */
.est-dropzone :deep(.dropzone) {
    min-height: auto; /* Dropzone 150px; FileUpload.vue scoped `.dropzone { min-height: auto }` kutunun köküne uyguluyordu */
    padding: 20px;
    border: 2px solid #e5e5e5;
    background: #fff;
    color: #777;
    font-family: Arial, sans-serif;
    letter-spacing: .2px;
    transition: .2s linear;
}

.est-dropzone :deep(.dropzone:hover) {
    background-color: #f6f6f6;
}

.est-dropzone :deep(.dropzone__message) {
    margin: 2em 0;
    text-align: center;
}

/* clientapp karo kuralları (Estesoft tokenları yerine sabit değerlerle) */
/* Paketin önizleme dili Vue 2'deki `dz-preview` görünümünün yerini aldı ve tema o eski
   sınıfları stillediği için önizlemeler paket varsayılanlarıyla kaldı: kutucuklar
   `margin: auto` ile satıra dağılıyor, PDF karosu 1px SİYAH kenarlık + 12px yarıçapla,
   görsel karosu kenarlıksız ve `height: auto` ile yarı boy geliyordu — aynı satırdaki iki
   dosya iki farklı dil konuşuyordu. Kurallar wrapper'da yaşar ki üç tüketici (dosya
   yükleme, WhatsApp şablonu, chatbot veri kaynağı) aynı görünümü paylaşsın. */
.est-dropzone :deep(.dropzone) {
    --preview-size: 104px;
}

/* Karolar soldan başlayıp sırayla dizilir; `margin: auto` onları kutunun boşluğuna
   dağıtıyordu (iki dosya, biri solda biri sağda). */
.est-dropzone :deep(.dropzone__box) {
    gap: 8px;
    align-content: flex-start;
}

.est-dropzone :deep(.dropzone__item--style) {
    margin: 0;
    padding: 0;
}

/* Tek karo geometrisi: kenarlıklı, 8px yarıçaplı beyaz kutu — küçük resmi olan da
   olmayan da aynı. */
.est-dropzone :deep(.dropzone__item-thumbnail),
.est-dropzone :deep(.dropzone__item--style:not(.dropzone--has-thumbnail) > .dropzone__item-thumbnail) {
    border: 1px solid var(--est-border, #dfe4ea);
    border-radius: 8px;
    background: #fff;
}

.est-dropzone :deep(.dropzone--has-thumbnail > .dropzone__item-thumbnail > img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Ad/boyut katmanı karonun tamamını kaplar: küçük resmi olmayan dosyada zemin,
   olanda hover'da açılan okunur bir örtü. */
.est-dropzone :deep(.dropzone__details),
.est-dropzone :deep(.dropzone__details--style) {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 3px;
    width: var(--preview-size);
    min-width: 0;
    max-width: none;
    height: var(--preview-size);
    padding: 8px;
    color: var(--est-ink, #23262f);
    font-size: 12px;
    line-height: 17px;
    text-align: left;
}

.est-dropzone :deep(.dropzone--has-thumbnail > .dropzone__details) {
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
}

.est-dropzone :deep(.dropzone__file-size) {
    margin-bottom: 0;
    font-size: 12px;
    font-weight: 600;
}

.est-dropzone :deep(.dropzone__filename) {
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--est-ink-2, #5a6474);
}

/* Paket her iki satırı da yarı saydam beyaz bir "vurgu" kutusuna alıyor; örtü zaten var. */
.est-dropzone :deep(.dropzone__file-size > span),
.est-dropzone :deep(.dropzone__filename > span) {
    padding: 0;
    background: none;
}

/* Uzantı etiketi ad satırının üstünde, karonun ortasında durur. */
.est-dropzone :deep(.dropzone__item:not(.dropzone--has-thumbnail)::before) {
    content: attr(data-ext);
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(var(--preview-size) - 34px);
    color: var(--est-ink-3, #8b95a6);
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

/* Paketin ilerleme göstergesi 80x16'lık, yarı saydam beyaz zeminli ve 8px yarıçaplı bir
   kutuya alınmış native `progress`ti: tarayıcının kendi yeşil dolgusu kutunun kırpmasıyla
   yarım görünüyor ve dosya adının üstüne biniyordu. Karonun ortasında ince bir raya
   çevrildi. */
.est-dropzone :deep(.dropzone__progress) {
    top: 50%;
    right: 8px;
    left: 8px;
    height: 5px;
    margin-top: -3px;
    margin-left: 0;
    border-radius: 3px;
    background: rgba(23, 26, 31, 0.12);
}

.est-dropzone :deep(.dropzone__progress .dropzone__progress-bar) {
    display: block;
    width: 100%;
    height: 5px;
    margin-top: 0;
    border: 0;
    border-radius: 3px;
    background: transparent;
    appearance: none;
    -webkit-appearance: none;
}

.est-dropzone :deep(.dropzone__progress-bar::-webkit-progress-bar) {
    background: transparent;
}

.est-dropzone :deep(.dropzone__progress-bar::-webkit-progress-value) {
    border-radius: 3px;
    background: #089bab;
}

.est-dropzone :deep(.dropzone__progress-bar::-moz-progress-bar) {
    border-radius: 3px;
    background: #089bab;
}

.est-dropzone :deep(.dropzone__item-controls) {
    top: 5px;
    right: 5px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.92);
}

.est-dropzone :deep(.dropzone__item-control) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    color: var(--est-ink-2, #5a6474);
}

    .est-dropzone :deep(.dropzone__item-control:hover) {
        color: #c0392b;
    }

.est-dropzone :deep(.dropzone__success-mark),
.est-dropzone :deep(.dropzone__error-mark) {
    top: 5px;
    left: 5px;
}

/* İşaretler `currentColor` kullanıyor; paket ikisini de nötr griyle basıyor. */
.est-dropzone :deep(.dropzone__success-mark .gg-check-o) {
    color: #089bab;
}

.est-dropzone :deep(.dropzone__error-mark) {
    color: #c0392b;
}
</style>
